<template>
  <div
    v-if="engagement && !loading"
    class="poll"
    data-test-id="poll"
    :class="pollClasses"
  >
    <button
      v-if="showNextButton"
      class="poll__next-button poll__next-button--top"
      :disabled="!nextEngagement"
      @click="moveToNextEngagement"
    >
      <span class="poll__next-button-text">{{ nextButtonText }}</span>
      <icon-component
        class="poll__next-button-icon"
        :class="nextEngagement ? 'poll__next-button-icon--arrow' : 'poll__next-button-icon--check'"
        :name="nextEngagement ? 'arrow' : 'check'"
        :title="nextEngagement ? 'arrow' : 'check'"
      />
    </button>
    <div class="poll__main-container">
      <div
        v-if="!showNextButton"
        class="poll__image-container"
      >
        <div
          ref="illustration"
          class="poll__icon"
          :class="{ 'poll__icon--animated': animationRunning }"
          v-html="svgMarkup"
        />
      </div>
      <component
        :is="showAuthWallCta ? 'EngagementResultAuthWall' : 'EngagementResultSubWall'"
        v-if="showAuthWallCta || showSubWallCta"
        :engagement-type="engagement.type"
      />
      <div v-else class="poll__content-container">
        <h2
          class="poll__heading"
          data-test-id="poll-heading"
        >Community Poll</h2>
        <p
          class="poll__question"
          data-test-id="poll-question"
        >{{ engagement.question | decodeHtml }}</p>
        <poll-results
          v-if="isAnswered"
          :poll-data="engagement || []"
          :voted-answers="engagement.userAnswers"
        />
        <poll-form
          v-else
          :options="engagement.options"
          :form-id="`${engagement.id}`"
          :is-multi-select="engagement.isMultiSelect"
          @optionSelected="refetchEngagement"
        />
      </div>
    </div>
    <lead-gen
      v-if="isAnswered && showLeadGen"
      id="poll-results-leadgen"
      :campaign="pollCampaignId"
      show-if-logged-in
    />
    <app-link
      v-show="isAnswered && !showLeadGen && ctaLink && !resultsRequireAuth && !resultsRequireKnown"
      :to="ctaLink"
      class="poll__cta-link link--unstyled"
      data-test-id="poll-cta-link"
    >
      {{ resultsText }}
      <icon-component
        name="caret"
        title="caret icon"
        class="poll__cta-icon"
      />
    </app-link>
    <button
      v-if="showNextButton"
      class="poll__next-button poll__next-button--side"
      :disabled="!nextEngagement"
      @click="moveToNextEngagement"
    >
      <span class="poll__next-button-text">{{ nextButtonText }}</span>
      <icon-component
        class="poll__next-button-icon"
        :class="nextEngagement ? 'poll__next-button-icon--arrow' : 'poll__next-button-icon--check'"
        :name="nextEngagement ? 'arrow' : 'check'"
        :title="nextEngagement ? 'next' : 'done'"
      />
    </button>
  </div>
</template>

<script>
import PollForm from '@/components/EngagementToolkit/PollForm.vue';
import PollResults from '@/components/EngagementToolkit/PollResults.vue';
import EngagementResultAuthWall from '@/components/EngagementToolkit/EngagementResultAuthWall.vue';
import EngagementResultSubWall from '@/components/EngagementToolkit/EngagementResultSubWall.vue';
import LeadGen from '@/components/Global/LeadGen.vue';
import AppLink from '@/components/Global/AppLink.vue';
import { GET_ENGAGEMENT } from '@/graphql/queries/engagement-toolkit-queries';
import { mapGetters } from 'vuex';
import engagementToolkitMixin from '@/mixins/engagement-toolkit-mixin';
import qualtricsLinkParamsMixin from '@/mixins/qualtrics-link-params-mixin';
import ButtonComponent from '@/components/Global/ButtonComponent.vue';

export default {
  name: 'PollComponent',
  components: {
    LeadGen,
    PollForm,
    PollResults,
    EngagementResultAuthWall,
    EngagementResultSubWall,
    AppLink,
    ButtonComponent,
  },
  mixins: [engagementToolkitMixin, qualtricsLinkParamsMixin],
  props: {
    pollData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      engagement: {},
      nextEngagement: null,
      iconName: 'poll',
      isFirstPoll: true,
      loading: true,
    };
  },
  computed: {
    ...mapGetters([
      'userIsLoggedIn',
      'userHasSubscribed',
      'userWasReferredByEmail',
    ]),
    showNextButton() {
      return (
        this.dynamicPollingEnabled
        && this.isAnswered
        && !this.resultsRequireKnown
      );
    },
    pollClasses() {
      return {
        'poll--auth-wall': this.showAuthWallCta,
        'poll--sub-wall': this.showSubWallCta,
        'poll--with-next-button': this.showNextButton,
      };
    },
    resultsRequireAuth() {
      return this.engagement.resultsRequireAuth && !this.userIsLoggedIn;
    },
    resultsRequireKnown() {
      return this.engagement.resultsRequireKnown && !this.userIsKnown;
    },
    selectedOption() {
      if (this.engagement?.userAnswers?.length > 0 && !this.engagement?.isMultiSelect) {
        const userAnswer = this.engagement.userAnswers[0];
        return this.engagement.options.find((option) => option.id === userAnswer) || null;
      }
      return null;
    },
    ctaLink() {
      const ctaLink = this.selectedOption?.ctaLink || this.engagement.ctaLink || '';

      return this.insertQualtricsParams(ctaLink);
    },
    ctaText() {
      return this.selectedOption?.ctaText || this.engagement.ctaText;
    },
    resultsText() {
      return this.ctaText || (this.showLeadGen ? 'Sign up for emails' : 'Read More');
    },
    pollCampaignId() {
      return `poll_${this.engagement.id}`;
    },
    showLeadGen() {
      return this.engagement.ctaLink === 'SIGNUP';
    },
    showAuthWallCta() {
      return this.isAnswered && this.resultsRequireAuth;
    },
    showSubWallCta() {
      return this.isAnswered && this.resultsRequireKnown;
    },
    dynamicPollingEnabled() {
      return this.engagement?.isSuggestable;
    },
    userIsKnown() {
      return this.userHasSubscribed || this.userWasReferredByEmail || this.userIsLoggedIn;
    },
    isAnswered() {
      return this.engagement?.userAnswers?.length > 0;
    },
    nextButtonText() {
      return this.nextEngagement ? 'Next poll' : 'You\'re caught up';
    },
  },
  watch: {
    userIsKnown(to, from) {
      if (
        !from && to
        && this.engagement?.isSuggestable
        && !this.nextEngagement
        && this.isAnswered
      ) {
        this.requestNextEngagement();
      }
    },
  },
  methods: {
    refetchEngagement(nextEngagement) {
      this.$apollo.queries.engagement.refetch()
        .then(() => {
          if (nextEngagement) {
            this.nextEngagement = nextEngagement;
          }
        });
    },
    moveToNextEngagement() {
      if (this.nextEngagement) {
        this.engagement = this.nextEngagement;
        this.nextEngagement = null;
      }
    },
    requestNextEngagement() {
      this.$apollo.query({
        query: GET_ENGAGEMENT,
        variables: {
          type: 'poll',
          getNextEngagement: true,
        },
      }).then((response) => {
        this.nextEngagement = response.data.engagement;
      });
    },
  },
  apollo: {
    // We need to make a query to get the engagement
    // userAnswers to keep track of if the user has
    // already answered this poll - so we can display
    // results instead of asking the same question.
    engagement: {
      query: GET_ENGAGEMENT,
      fetchPolicy: 'no-cache',
      variables() {
        const fetchingCurrent = this.isFirstPoll && this.pollData.isCurrent;
        const id = fetchingCurrent ? null : (this.engagement?.id || this.pollData.id);
        return {
          id,
          type: 'poll',
          isFirstPoll: this.isFirstPoll,
          userIsKnown: this.userIsKnown,
        };
      },
      result() {
        this.isFirstPoll = false;
        this.loading = false;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_poll';
</style>

<style lang="scss"> // no scoped to reach the svg elements within v-html
  @import '@/stylesheets/components/_poll-icon'; // poll.svg's animation

  @keyframes poll-next-button-arrow-nudge {
    0% {transform: translateX(0px) }
    17% {transform: translateX(7px) }
    20% {transform: translateX(-2px) }
    22% {transform: translateX(0px) }
    100% {transform: translateX(0px) }
  }

  .poll__next-button-icon--arrow svg {
    animation: poll-next-button-arrow-nudge 5s ease-in-out infinite;
  }
</style>
